(function () {
  'use strict';

  if (customElements.get('funny-balaclava-builder')) return;

  var catalogRequests = new Map();

  function readJSON(root, selector) {
    var node = root.querySelector(selector);
    if (!node) throw new Error('Missing builder configuration.');
    return JSON.parse(node.textContent);
  }

  function packSize(value) {
    var match = /^([1-6])\s+PCS?$/i.exec(String(value || '').trim());
    return match ? Number(match[1]) : 0;
  }

  function normalizeCatalog(value) {
    if (!value || !Array.isArray(value.groups) || value.groups.length !== 7) throw new Error('Invalid mask groups.');
    var groupIds = new Set();
    var designIds = new Set();
    var designs = [];
    var groups = value.groups.map(function (group) {
      var id = Number(group.id);
      if (!Number.isInteger(id) || id < 1 || id > 7 || groupIds.has(id) || typeof group.name !== 'string' || !group.name.trim() || !Array.isArray(group.designs) || !group.designs.length) throw new Error('Invalid mask group.');
      groupIds.add(id);
      group.designs.forEach(function (design, index) {
        if (!design || typeof design.id !== 'string' || !design.id.trim() || designIds.has(design.id) || typeof design.name !== 'string' || !design.name.trim() || typeof design.image !== 'string') throw new Error('Invalid mask design.');
        var image = new URL(design.image);
        if (image.protocol !== 'https:') throw new Error('Invalid mask image.');
        designIds.add(design.id);
        designs.push({ id: design.id, name: design.name.trim(), image: image.href, groupId: String(id), styleNumber: index + 1 });
      });
      return { id: String(id), name: group.name.trim(), image: group.designs[0].image };
    });
    if (designs.length !== 31) throw new Error('Incomplete mask catalog.');
    groups.sort(function (left, right) { return Number(left.id) - Number(right.id); });
    designs.sort(function (left, right) { return Number(left.groupId) - Number(right.groupId) || left.styleNumber - right.styleNumber; });
    return { groups: groups, designs: designs };
  }

  function thumbnailURL(source, width) {
    var url = new URL(source);
    url.searchParams.set('width', String(width));
    return url.href;
  }

  function loadCatalog(url) {
    if (!catalogRequests.has(url)) {
      var request = fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error('Could not load mask catalog.');
        return response.json();
      }).then(normalizeCatalog).catch(function (error) {
        catalogRequests.delete(url);
        throw error;
      });
      catalogRequests.set(url, request);
    }
    return catalogRequests.get(url);
  }

  class FunnyBalaclavaBuilder extends HTMLElement {
    connectedCallback() {
      window.clearTimeout(this.connectTimer);
      this.connectTimer = window.setTimeout(() => this.initialize(), 0);
    }

    disconnectedCallback() {
      window.clearTimeout(this.connectTimer);
      window.clearTimeout(this.trayTimer);
      window.cancelAnimationFrame(this.carouselFrame);
      if (this.events) this.events.abort();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.connectionVersion = (this.connectionVersion || 0) + 1;
    }

    initialize() {
      if (!this.isConnected) return;
      if (this.events) this.events.abort();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.events = new AbortController();
      this.connectionVersion = (this.connectionVersion || 0) + 1;
      this.form = this.closest('form');
      this.validation = this.querySelector('[data-pack-validation]');
      this.feedback = this.querySelector('[data-feedback]');
      this.retry = this.querySelector('[data-retry]');
      this.grid = this.querySelector('[data-design-grid]');
      this.selection = Array.isArray(this.selection) ? this.selection : [];
      this.filter = this.filter || '1';
      this.ready = false;
      this.failed = false;
      this.configured = false;
      this.variantKey = '';
      try {
        this.strings = readJSON(this, '[data-mask-translations]');
        this.variants = readJSON(this, '[data-mask-variants]');
        this.optionPosition = Number(this.dataset.packOptionPosition);
        if (!this.form || !this.validation || !Number.isInteger(this.optionPosition) || this.optionPosition < 1 || this.optionPosition > 3 || !Array.isArray(this.variants) || this.variants.length !== 6) throw new Error('Invalid pack configuration.');
        var packs = new Set();
        this.variants.forEach((variant) => {
          var pack = packSize(variant.options && variant.options[this.optionPosition - 1]);
          if (!pack || packs.has(pack) || !variant.id || typeof variant.available !== 'boolean' || !Number.isFinite(Number(variant.price)) || Number(variant.price) < 0) throw new Error('Invalid pack variant.');
          packs.add(pack);
        });
        if (packs.size !== 6) throw new Error('Incomplete pack variants.');
        this.configured = true;
      } catch (error) {
        this.showFailure();
        return;
      }

      var listenerOptions = { signal: this.events.signal };
      this.addEventListener('click', (event) => this.handleClick(event), listenerOptions);
      this.addEventListener('keydown', (event) => this.handleCarouselKey(event), listenerOptions);
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[name="option' + this.optionPosition + '"]')) this.refreshVariant();
      }, listenerOptions);
      this.form.addEventListener('submit', (event) => {
        this.refreshVariant();
        this.updateSelection();
        if (!this.isComplete()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.feedback.focus();
        }
      }, { capture: true, signal: this.events.signal });
      this.form.addEventListener('invalid', (event) => {
        if (event.target !== this.validation) return;
        event.preventDefault();
        this.feedback.focus();
      }, { capture: true, signal: this.events.signal });
      this.observer = new MutationObserver(() => this.refreshVariant());
      this.observer.observe(this.form, { childList: true, subtree: true });
      this.resizeObserver = new ResizeObserver(() => this.scheduleCarouselUpdate());
      this.resizeObserver.observe(this);
      this.refreshVariant();
      this.fetchCatalog();
    }

    text(key, values) {
      var value = String((this.strings || {})[key] || '');
      Object.keys(values || {}).forEach(function (name) {
        value = value.split('__' + name + '__').join(String(values[name]));
      });
      return value;
    }

    async fetchCatalog() {
      this.ready = false;
      this.failed = false;
      this.retry.hidden = true;
      this.setAttribute('aria-busy', 'true');
      this.updateSelection();
      var version = this.connectionVersion;
      try {
        var catalog = await loadCatalog(this.dataset.catalogUrl);
        if (!this.isConnected || version !== this.connectionVersion) return;
        this.catalog = catalog;
        this.designsById = new Map(catalog.designs.map(function (design) { return [design.id, design]; }));
        this.buildCatalog();
        this.ready = true;
        this.setAttribute('aria-busy', 'false');
        this.updateSelection();
      } catch (error) {
        if (!this.isConnected || version !== this.connectionVersion) return;
        this.showFailure();
      }
    }

    showFailure() {
      this.ready = false;
      this.failed = true;
      this.setAttribute('aria-busy', 'false');
      this.setAttribute('data-state', 'error');
      var message = this.text('load_error') || (this.feedback && this.feedback.dataset.loadError) || '';
      if (this.feedback) this.feedback.textContent = message;
      if (this.validation) {
        this.validation.value = '';
        this.validation.setCustomValidity(message);
      }
      this.querySelectorAll('[data-mask-property]').forEach(function (input) {
        input.disabled = true;
        input.value = '';
      });
      if (this.configured) this.updateSelection();
      if (this.retry) this.retry.hidden = !this.configured;
    }

    refreshVariant() {
      if (!this.form || !this.variants) return;
      var controls = this.form.querySelectorAll('[name="option' + this.optionPosition + '"]');
      var selected = Array.from(controls).find(function (control) { return control.tagName === 'SELECT' || control.checked; });
      var variantInput = this.form.querySelector('[name="id"]');
      var selectedId = variantInput ? variantInput.value : this.dataset.initialVariantId;
      var variant = selected
        ? this.variants.find((item) => item.options[this.optionPosition - 1] === selected.value)
        : this.variants.find(function (item) { return String(item.id) === String(selectedId); });
      var key = variant ? String(variant.id) + ':' + variant.available + ':' + variant.price : 'unavailable';
      if (variant && variantInput && variantInput.value !== String(variant.id)) variantInput.value = String(variant.id);
      if (key === this.variantKey) return;
      this.variantKey = key;
      this.variant = variant || null;
      this.target = variant ? packSize(variant.options[this.optionPosition - 1]) : 0;
      this.updateSelection();
    }

    buildCatalog() {
      var filters = this.querySelector('[data-group-filters]');
      filters.replaceChildren();
      if (!this.catalog.groups.some((group) => group.id === this.filter)) this.filter = this.catalog.groups[0].id;
      this.catalog.groups.forEach((group) => {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'funny-balaclava-builder__filter';
        button.dataset.groupFilter = group.id;
        button.setAttribute('aria-label', this.text('group_heading', { number: group.id, name: group.name }));
        var image = document.createElement('img');
        image.src = thumbnailURL(group.image, 72);
        image.alt = '';
        image.width = 44;
        image.height = 44;
        image.loading = 'lazy';
        image.decoding = 'async';
        button.appendChild(image);
        var text = document.createElement('span');
        text.className = 'funny-balaclava-builder__filter-text';
        var line = document.createElement('span');
        line.className = 'funny-balaclava-builder__filter-line';
        line.textContent = this.text('line_label', { number: group.id });
        text.appendChild(line);
        var family = document.createElement('strong');
        family.textContent = this.strings.families[group.id];
        text.appendChild(family);
        button.appendChild(text);
        filters.appendChild(button);
      });
      this.grid.replaceChildren();
      this.catalog.groups.forEach((group) => {
        var section = document.createElement('section');
        section.className = 'funny-balaclava-builder__group';
        section.dataset.designGroup = group.id;
        var heading = document.createElement('h3');
        heading.className = 'funny-balaclava-builder__group-heading';
        heading.id = this.validation.id + '-group-' + group.id;
        heading.textContent = this.text('group_heading', { number: group.id, name: group.name });
        section.setAttribute('aria-labelledby', heading.id);
        section.appendChild(heading);
        var grid = document.createElement('ul');
        grid.className = 'funny-balaclava-builder__grid';
        grid.dataset.designTrack = group.id;
        grid.id = heading.id + '-designs';
        grid.tabIndex = 0;
        grid.setAttribute('aria-labelledby', heading.id);
        this.catalog.designs.filter(function (design) { return design.groupId === group.id; }).forEach((design) => {
          grid.appendChild(this.designNode(design));
        });
        section.appendChild(grid);
        var navigation = document.createElement('div');
        navigation.className = 'funny-balaclava-builder__carousel-nav';
        navigation.dataset.carouselNav = '';
        navigation.hidden = this.catalog.designs.filter(function (design) { return design.groupId === group.id; }).length <= 4;
        ['previous', 'next'].forEach((direction, index) => {
          if (index === 1) {
            var range = document.createElement('span');
            range.className = 'funny-balaclava-builder__carousel-range';
            range.dataset.carouselRange = '';
            range.setAttribute('role', 'status');
            range.setAttribute('aria-live', 'polite');
            navigation.appendChild(range);
          }
          var arrow = document.createElement('button');
          arrow.type = 'button';
          arrow.className = 'funny-balaclava-builder__carousel-arrow';
          arrow.dataset.carouselDirection = direction;
          arrow.setAttribute('aria-label', this.text(direction + '_designs'));
          arrow.setAttribute('aria-controls', grid.id);
          arrow.textContent = direction === 'previous' ? '\u2039' : '\u203a';
          navigation.appendChild(arrow);
        });
        section.appendChild(navigation);
        grid.addEventListener('scroll', () => this.scheduleCarouselUpdate(), { passive: true, signal: this.events.signal });
        this.bindCarouselDrag(grid);
        this.grid.appendChild(section);
      });
      this.applyFilter();
    }

    designNode(design) {
      var item = document.createElement('li');
      item.className = 'funny-balaclava-builder__card';
      item.dataset.designCard = design.id;
      item.dataset.groupId = design.groupId;
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'funny-balaclava-builder__design-button';
      button.dataset.designAction = 'add';
      button.dataset.designId = design.id;
      button.setAttribute('aria-label', this.text('add_design', { name: design.name }));
      var media = document.createElement('span');
      media.className = 'funny-balaclava-builder__card-media';
      var image = document.createElement('img');
      image.className = 'funny-balaclava-builder__image';
      image.src = thumbnailURL(design.image, 240);
      image.alt = design.name;
      image.width = 240;
      image.height = 300;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      media.appendChild(image);
      var badge = document.createElement('span');
      badge.className = 'funny-balaclava-builder__badge';
      badge.dataset.designBadge = design.id;
      badge.setAttribute('aria-hidden', 'true');
      badge.hidden = true;
      media.appendChild(badge);
      button.appendChild(media);
      var title = document.createElement('span');
      title.className = 'funny-balaclava-builder__design-name';
      title.textContent = this.text('style_label', { number: design.styleNumber });
      title.title = design.name;
      button.appendChild(title);
      item.appendChild(button);
      return item;
    }

    handleClick(event) {
      var button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-retry')) {
        this.fetchCatalog();
        return;
      }
      if (button.hasAttribute('data-group-filter')) {
        this.filter = button.dataset.groupFilter;
        this.applyFilter();
        return;
      }
      if (button.hasAttribute('data-carousel-direction')) {
        var track = button.closest('[data-design-group]').querySelector('[data-design-track]');
        this.scrollCarousel(track, button.dataset.carouselDirection === 'next' ? 1 : -1);
        return;
      }
      if (button.hasAttribute('data-remove-mask')) {
        var slotIndex = Number(button.dataset.removeMask);
        if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= this.selection.length) return;
        this.selection.splice(slotIndex, 1);
        this.updateSelection();
        var removeButtons = this.querySelectorAll('[data-remove-mask]');
        var focusTarget = removeButtons[Math.min(slotIndex, removeButtons.length - 1)] || this.querySelector('[data-group-filter="' + this.filter + '"]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        return;
      }
      if (!this.ready || !button.dataset.designAction || !this.designsById.has(button.dataset.designId)) return;
      if (event.detail !== 0 && Date.now() < (this.suppressDesignClickUntil || 0)) return;
      this.refreshVariant();
      var id = button.dataset.designId;
      if (!this.variant || !this.variant.available || this.selectedCount() >= this.target) return;
      this.selection.push(id);
      this.updateSelection();
      var tray = this.querySelector('.funny-balaclava-builder__tray');
      tray.classList.remove('is-updated');
      void tray.offsetWidth;
      tray.classList.add('is-updated');
      window.clearTimeout(this.trayTimer);
      this.trayTimer = window.setTimeout(() => tray.classList.remove('is-updated'), 600);
      tray.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    applyFilter() {
      this.querySelectorAll('[data-group-filter]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.groupFilter === this.filter ? 'true' : 'false');
      });
      this.grid.querySelectorAll('[data-design-group]').forEach((group) => {
        group.hidden = group.dataset.designGroup !== this.filter;
      });
      this.scheduleCarouselUpdate();
    }

    scheduleCarouselUpdate() {
      window.cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = window.requestAnimationFrame(() => {
        this.grid.querySelectorAll('[data-design-group]:not([hidden])').forEach((group) => this.updateCarousel(group));
      });
    }

    updateCarousel(group) {
      var track = group.querySelector('[data-design-track]');
      var navigation = group.querySelector('[data-carousel-nav]');
      if (!track || !navigation || !track.clientWidth) return;
      var cards = track.children;
      var count = cards.length;
      navigation.hidden = count <= 4;
      if (navigation.hidden) return;
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      var position = Math.min(max, Math.max(0, track.scrollLeft));
      var step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
      var start = Math.min(Math.max(0, count - 4), Math.round(position / step));
      navigation.querySelector('[data-carousel-direction="previous"]').disabled = position < 2;
      navigation.querySelector('[data-carousel-direction="next"]').disabled = max - position < 2;
      var range = navigation.querySelector('[data-carousel-range]');
      var message = this.text('design_range', { start: start + 1, end: Math.min(count, start + 4), count: count });
      if (range.textContent !== message) range.textContent = message;
    }

    scrollCarousel(track, direction, destination) {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      var left = typeof destination === 'number' ? destination : track.scrollLeft + direction * (track.clientWidth + gap);
      track.scrollTo({ left: Math.min(max, Math.max(0, left)), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    handleCarouselKey(event) {
      var track = event.target;
      if (!track.hasAttribute('data-design-track')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      var destination = event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth : undefined;
      this.scrollCarousel(track, event.key === 'ArrowLeft' ? -1 : 1, destination);
    }

    bindCarouselDrag(track) {
      var drag = null;
      var options = { signal: this.events.signal };
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button:not([data-design-action="add"])') || track.scrollWidth <= track.clientWidth) return;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      }, options);
      track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        if (!(event.buttons & 1)) {
          finish();
          return;
        }
        var distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        event.preventDefault();
        track.scrollLeft = drag.left - distance;
      }, options);
      var finish = () => {
        if (!drag) return;
        var completed = drag;
        drag = null;
        if (track.hasPointerCapture(completed.id)) track.releasePointerCapture(completed.id);
        track.classList.remove('is-dragging');
        if (completed.moved && track.children.length > 1) {
          this.suppressDesignClickUntil = Date.now() + 300;
          var step = track.children[1].offsetLeft - track.children[0].offsetLeft;
          this.scrollCarousel(track, 0, Math.round(track.scrollLeft / step) * step);
        }
      };
      track.addEventListener('pointerup', finish, options);
      track.addEventListener('pointercancel', finish, options);
      track.addEventListener('lostpointercapture', finish, options);
      track.addEventListener('pointerleave', () => {
        if (drag && !drag.moved) finish();
      }, options);
      this.events.signal.addEventListener('abort', () => {
        var stopped = drag;
        drag = null;
        track.classList.remove('is-dragging');
        if (stopped && track.hasPointerCapture(stopped.id)) track.releasePointerCapture(stopped.id);
      }, { once: true });
    }

    selectedCount() {
      return this.selection.length;
    }

    isComplete() {
      return Boolean(this.ready && !this.failed && this.variant && this.variant.available && this.target > 0 && this.selectedCount() === this.target);
    }

    formatMoney(cents) {
      var currency = this.dataset.currency;
      try {
        return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(cents / 100);
      } catch (error) {
        return currency + ' ' + (cents / 100).toFixed(2);
      }
    }

    updateTray(selectedDesigns) {
      var slots = this.querySelector('[data-pack-slots]');
      if (!slots) return;
      var key = String(this.target || 0) + ':' + selectedDesigns.map(function (design) { return design.id; }).join('|');
      if (key === this.trayKey) return;
      this.trayKey = key;
      slots.replaceChildren();
      for (var index = 0; index < Math.max(this.target || 0, selectedDesigns.length); index += 1) {
        var item = document.createElement('li');
        item.className = 'funny-balaclava-builder__slot';
        var design = selectedDesigns[index];
        if (design) {
          item.classList.add('is-filled');
          item.classList.toggle('is-extra', index >= this.target);
          item.setAttribute('aria-label', this.text('filled_slot', { number: index + 1, name: design.name }));
          var image = document.createElement('img');
          image.src = thumbnailURL(design.image, 72);
          image.alt = design.name;
          image.width = 48;
          image.height = 60;
          image.decoding = 'async';
          image.draggable = false;
          item.appendChild(image);
          var remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'funny-balaclava-builder__slot-remove';
          remove.dataset.removeMask = String(index);
          remove.setAttribute('aria-label', this.text('remove_slot', { number: index + 1, name: design.name }));
          remove.textContent = '\u00d7';
          item.appendChild(remove);
          var slotNumber = document.createElement('span');
          slotNumber.className = 'funny-balaclava-builder__slot-number';
          slotNumber.setAttribute('aria-hidden', 'true');
          slotNumber.textContent = String(index + 1);
          item.appendChild(slotNumber);
        } else {
          item.setAttribute('aria-label', this.text('empty_slot', { number: index + 1 }));
          var number = document.createElement('span');
          number.textContent = String(index + 1);
          item.appendChild(number);
        }
        slots.appendChild(item);
      }
    }

    updateSelection() {
      if (!this.validation || !this.strings) return;
      var count = this.selectedCount();
      var complete = this.isComplete();
      var message;
      if (this.failed) message = this.text('load_error');
      else if (!this.ready) message = this.text('loading');
      else if (!this.variant || !this.variant.available) message = this.text('unavailable');
      else if (count > this.target) message = this.text('overfilled', { count: count - this.target });
      else if (count < this.target) message = this.text('remaining', { count: this.target - count });
      else message = this.text('ready');
      this.validation.value = complete ? String(count) : '';
      this.validation.setCustomValidity(complete ? '' : message);
      this.validation.setAttribute('aria-invalid', complete ? 'false' : 'true');
      this.dataset.state = complete ? 'complete' : (this.failed ? 'error' : 'incomplete');
      if (this.feedback.textContent !== message) this.feedback.textContent = message;
      var countText = this.text('selected_count', { count: count, pack: this.target || 0 });
      var countNode = this.querySelector('[data-selected-count]');
      if (countNode.textContent !== countText) countNode.textContent = countText;
      var selectedDesigns = [];
      if (this.designsById) selectedDesigns = this.selection.map((id) => this.designsById.get(id)).filter(Boolean);
      this.querySelectorAll('[data-mask-property]').forEach(function (input, index) {
        input.disabled = !complete || index >= selectedDesigns.length;
        input.value = input.disabled ? '' : selectedDesigns[index].name;
      });
      this.updateTray(selectedDesigns);
      this.querySelectorAll('[data-design-card]').forEach((card) => {
        var quantity = this.selection.filter(function (id) { return id === card.dataset.designCard; }).length;
        card.querySelector('[data-design-action="add"]').setAttribute('aria-disabled', !this.ready || !this.variant || !this.variant.available || count >= this.target ? 'true' : 'false');
        card.classList.toggle('is-selected', quantity > 0);
        var badge = card.querySelector('[data-design-badge]');
        if (badge) {
          badge.hidden = quantity === 0;
          var badgeText = '\u2713';
          if (badge.textContent !== badgeText) badge.textContent = badgeText;
        }
      });
    }
  }

  customElements.define('funny-balaclava-builder', FunnyBalaclavaBuilder);
})();
